import { useEffect, useState } from "react";
import { listCalculations, deleteCalculation } from "@/lib/api";
import { Trash2, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function HistoryPanel({ refreshKey, onLoad }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    listCalculations()
      .then((data) => {
        if (alive) setItems(data);
      })
      .catch(() => toast.error("Failed to load history"))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
    // Only re-run when refreshKey changes. `listCalculations` and `toast` are stable
    // module imports; setItems / setLoading are stable state setters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  const remove = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteCalculation(id);
      setItems((s) => s.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  if (loading) {
    return (
      <div className="p-6 mono text-sm text-[#64748B]">Loading…</div>
    );
  }
  if (!items.length) {
    return (
      <div className="p-8 text-center text-[#64748B] text-sm">
        No saved calculations yet. Save one from the load plan view.
      </div>
    );
  }
  return (
    <div className="divide-y divide-white/5" data-testid="history-list">
      {items.map((it) => {
        const s = it.result?.summary || {};
        const d = new Date(it.created_at);
        return (
          <div
            key={it.id}
            data-testid={`history-item-${it.id}`}
            onClick={() => onLoad(it)}
            className="p-4 cursor-pointer hover:bg-white/[0.03] transition-colors duration-200 group"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="heading font-semibold truncate">{it.name}</div>
                <div className="mono text-[11px] text-[#64748B] mt-1">
                  {d.toLocaleString()} · {s.total_cases || 0} cases · {s.total_vehicles || 0} vehicles
                </div>
                <div className="flex gap-3 mono text-[11px] mt-2 text-[#64748B]">
                  <span>{s.trailer_40ft_count || 0} × 40ft</span>
                  <span>{s.truck_32ft_count || 0} × 32ft</span>
                  <span>{s.hydraulic_axle_count || 0} × axle</span>
                </div>
              </div>
              <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <ArrowUpRight className="w-4 h-4 text-[#2563EB]" />
                <button
                  data-testid={`delete-history-${it.id}`}
                  onClick={(e) => remove(it.id, e)}
                  className="text-slate-400 hover:text-[#EF4444] transition-colors duration-200"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
